import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, type ReactNode } from 'react';
import { ApiError, get, post, setUnauthorizedHandler } from './api';
import type { Me, Role } from './types';

interface AuthState {
  me: Me | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<Me>;
  logout: () => Promise<void>;
  refresh: () => Promise<unknown>;
}

const AuthContext = createContext<AuthState | null>(null);

const RANK: Record<Role, number> = { viewer: 0, user: 1, admin: 2, superadmin: 3 };

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        const me = await get<Me & { apiKey?: unknown }>('/auth/me');
        return me.user ? me : null;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  // Si cualquier petición devuelve 401 (sesión caducada o revocada), volver al login.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
      qc.setQueryData(['me'], null);
    });
  }, [qc]);

  /** Descarta los datos de la sesión anterior (otro usuario puede ver otras cosas). */
  const clearOtherQueries = useCallback(() => qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' }), [qc]);

  const login = useCallback(
    async (email: string, password: string) => {
      const me = await post<Me>('/auth/login', { email, password });
      // No usar qc.clear(): eliminaría la consulta 'me' a la que está suscrito el componente.
      clearOtherQueries();
      qc.setQueryData(['me'], me);
      return me;
    },
    [qc, clearOtherQueries],
  );

  const logout = useCallback(async () => {
    await post('/auth/logout').catch(() => undefined);
    clearOtherQueries();
    qc.setQueryData(['me'], null);
  }, [qc, clearOtherQueries]);

  return (
    <AuthContext.Provider
      value={{ me: meQuery.data ?? null, loading: meQuery.isLoading, login, logout, refresh: () => meQuery.refetch() }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth fuera de AuthProvider');
  return ctx;
}

/** Usuario autenticado (solo dentro de rutas protegidas). */
export function useMe(): Me {
  const { me } = useAuth();
  if (!me) throw new Error('useMe sin sesión');
  return me;
}

export const hasRole = (role: Role, min: Role) => RANK[role] >= RANK[min];

export function usePermissions() {
  const { user } = useMe();
  const isSuper = user.role === 'superadmin';
  return {
    isSuper,
    isAdmin: hasRole(user.role, 'admin'),
    canUpload: isSuper || (user.groupId !== null && hasRole(user.role, 'user')),
    canModifyJob: (job: { groupId: number; createdBy: { type: string; id: number } | null }) =>
      isSuper ||
      (user.role === 'admin' && job.groupId === user.groupId) ||
      (user.role === 'user' && job.createdBy?.type === 'user' && job.createdBy.id === user.id),
  };
}
